import { useMemo } from 'react';
import { useAppRoles } from './useAppRoles';
import { ALL_ROUTES, findRouteByPath, type AppRoute } from '../constants/appRoutes';

export interface UseRouteAccessResult {
  /** True when the current user holds at least one of the route's roles. */
  canAccess: (path: string) => boolean;
  /** Registry routes the current user may open, in registry order. */
  accessibleRoutes: AppRoute[];
  /** First route the user may open, used for `/` and the catch-all redirect. */
  defaultRoute: AppRoute | undefined;
  /**
   * Best alternative to offer when a route is blocked: prefer an accessible
   * route in the same area, then the first accessible route overall.
   */
  suggestedRoute: (fromPath: string) => AppRoute | undefined;
}

/**
 * Resolves what the current user can reach, based on the route registry and the
 * app roles on their ID token. Drives the landing redirect, the side navigation
 * and the Access Denied entitlement page, so all three stay in lockstep.
 */
export function useRouteAccess(): UseRouteAccessResult {
  const { roles, hasAnyRole } = useAppRoles();

  const accessibleRoutes = useMemo(
    () => ALL_ROUTES.filter((route) => route.roles.some((r) => roles.includes(r))),
    [roles]
  );

  /**
   * Routes eligible to be offered to the user on their behalf. Excludes support
   * pages, which every role can open but which are never the page someone is
   * trying to get to.
   */
  const defaultCandidates = useMemo(
    () => accessibleRoutes.filter((route) => !route.excludeFromDefaults),
    [accessibleRoutes]
  );

  const canAccess = (path: string) => {
    const route = findRouteByPath(path);
    return route ? hasAnyRole(route.roles) : true;
  };

  const suggestedRoute = (fromPath: string) => {
    const blocked = findRouteByPath(fromPath);
    const sameGroup = blocked
      ? defaultCandidates.find((route) => route.group === blocked.group)
      : undefined;
    return sameGroup ?? defaultCandidates[0];
  };

  return {
    canAccess,
    accessibleRoutes,
    defaultRoute: defaultCandidates[0],
    suggestedRoute,
  };
}
